"use client"

import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { CalendarPlus} from 'lucide-react'
import { trackEvent } from '@/lib/analytics'

type HowToBookSectionProps = {
  title?: string
  subtitle?: string
}

type Step = {
  icon: typeof CalendarPlus
  title: string
  description: string
  action: string
  actionLocation: string
}

export function HowToBookSection({
  title = 'How to Book',
  subtitle = 'You can book online any time via the links below, or contact the clinic if you have questions before booking.',
}: HowToBookSectionProps) {
  const openBooking = (location: string) => {
    try {
      trackEvent('book_appointment_click', { location })
    } catch {}
    if (typeof window !== 'undefined' && (window as any).openBookingModal) {
      ;(window as any).openBookingModal(location)
    }
  }

  const steps: Step[] = [
    {
      icon: CalendarPlus,
      title: 'Book Noosa Clinic',
      description: '',
      action: 'Book Appointment',
      actionLocation: 'how_to_book_noosa',
    },
    {
      icon: CalendarPlus,
      title: 'Book Maroochydore Clinic',
      description: '',
      action: 'Book Appointment',
      actionLocation: 'how_to_book_maroochydore',
    },
    {
      icon: CalendarPlus,
      title: 'Free 15min Phone Consult',
      description: '',
      action: 'Book Phone Consult',
      actionLocation: 'how_to_book_free_consult',
    },
  ]

  return (
    <section id="how-to-book-section" className="pt-20 pb-16 bg-gray-50">
      <div className="container mx-auto px-4 sm:w-2/3 md:w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141] max-w-[900px] mx-auto">
            {title}
          </h2>
          {subtitle && (
            <p className="text-gray-600 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center rounded-3xl p-8 bg-white border border-gray-200 overflow-hidden flex flex-col h-full"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-[#a48b61]/10 rounded-full p-4 mb-2">
                    <Icon className="h-6 w-6 text-[#a48b61]" />
                  </div>
                </div>
                <h3 className="text-xl font-light text-[#3f4141] mb-2 min-h-[3rem]">
                  {step.title}
                </h3>
                <p className="text-gray-600 mb-0 text-sm min-h-[2rem] line-clamp-2">
                  {step.description}
                </p>
                <div className="mt-auto sm:w-2/3 mx-auto">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => openBooking(step.actionLocation)}
                  >
                    {step.action}
                  </Button>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

